import { graphql, PageProps } from 'gatsby';
import { IGatsbyImageData } from 'gatsby-plugin-image';
import React from 'react';
import Layout from 'src/elements/layout/layout';
import { MarkupElement } from 'src/functions/formatElement.htmlAst';
import CallForActionWithDescription from 'src/molecules/callToAction/callToAction.withDescription';
import StoriesOfAwesomeList from 'src/organisms/articles/caseStudies/caseStudies.list';
import FeaturedCarousel from 'src/organisms/articles/caseStudies/featured.carousel';
import FeaturedSingle from 'src/organisms/articles/caseStudies/featured.single';
import BannerCaseStudies from 'src/organisms/banners/banner.caseStudies';
import PromotionModal from 'src/organisms/promotion/PromotionModal';

interface StoriesOfAwesomeProps extends PageProps {
  data: {
    allContentfulCaseStudy: {
      edges: {
        node: {
          title: string;
          slug: string;
          featuredDescriptionPreview?: {
            childMarkdownRemark: {
              htmlAst: {
                type: string;
                children: MarkupElement[]
              }
            }
          }
          body: {
            childMarkdownRemark: {
              htmlAst: {
                type: string;
                children: MarkupElement[]
              }
            }
          }
          heroImage: { gatsbyImageData: IGatsbyImageData };
        }
      }[]
    }
    contentfulPage: {
      seoDescription: string;
      seoTitle: string;
      title: string;
      subtitle: string;
      displayPromotionModal?: boolean;
      seoImage?: {
        file?: {
          url: string;
        }
      }
    }
  }
}

const StoriesOfAwesomePage = ({ data, location }: StoriesOfAwesomeProps): JSX.Element => {

  const helmetData = {
    title: data.contentfulPage.seoTitle,
    description: data.contentfulPage.seoDescription,
    previewImageURL: data.contentfulPage.seoImage?.file?.url
  }
  const displayModal = data.contentfulPage?.displayPromotionModal;

  const mobileFeatured = data.allContentfulCaseStudy?.edges[ 0 ]?.node;

  return (
    <Layout helmet={helmetData} location={location}>
      <BannerCaseStudies
        title={data.contentfulPage.title}
        description={data.contentfulPage.subtitle}
      />
      <FeaturedCarousel
        posts={data.allContentfulCaseStudy.edges.map((edge) => ({
          title: edge.node.title,
          description: edge.node.body?.childMarkdownRemark,
          slug: edge.node.slug,
          featuredDescriptionPreview: edge.node.featuredDescriptionPreview?.childMarkdownRemark,
          image: edge.node.heroImage.gatsbyImageData
        }))}
      />
      <FeaturedSingle
        image={mobileFeatured.heroImage}
        title={mobileFeatured.title}
        slug={mobileFeatured.slug}
      />
      <StoriesOfAwesomeList posts={data.allContentfulCaseStudy.edges.map((edge) => ({
        title: edge.node.title,
        description: edge.node.body?.childMarkdownRemark,
        slug: edge.node.slug,
        image: edge.node.heroImage.gatsbyImageData
      }))} />

      <CallForActionWithDescription
        title={`Get started for free`}
        description={`Start with our 14 day free trial and discover what The Business Plan can offer to you, your brand and your community.`}
        buttonSize="1em"
        actionButton={{
          id: '1',
          text: 'Start Your 14-Day Free Trial',
          helpText: `No credit card required.`,
          variant: [ 'primary' ],
          url: 'https://new.mn.co/?plan=free-trial',
          internal: {
            type: 'ContentfulButton'
          }
        }}
      />
      <PromotionModal displayModal={displayModal}/>
    </Layout>
  )
}

export const pageQuery = graphql`
  query CaseStudiesPageQuery {
    allContentfulCaseStudy(sort: { fields: createdAt, order: DESC }) {
      edges {
        node {
          title
          members
          featuredDescriptionPreview {
            childMarkdownRemark {
              htmlAst
            }
          }
          heroImage {
            gatsbyImageData(width: 872, layout: CONSTRAINED, placeholder: BLURRED)
          }
          slug
          body {
            childMarkdownRemark {
              htmlAst
            }
          }
        }
      }
    }
    contentfulPage(contentful_id: {eq: "5K4LikLl7Co8ZJiFm3PhJc"}) {
      title
      subtitle
      seoTitle
      seoDescription
      displayPromotionModal
      seoImage {
        file {
          url
        }
      }
    }
  }
`

export default StoriesOfAwesomePage;
